import { type ReactElement, useState, useCallback, useMemo } from 'react';
import { Typography, PrimaryButton, SecondaryButton } from '@weareopenr/component-library';
import {
  SelectTemplateStep,
  SelectMealtimeStep,
  SelectVenueStep,
  MenuTitleStep,
  SelectPriceSchemeStep,
  TruncationWarningsStep,
  SummaryStep,
} from './steps';
import {
  type DesignPlatformExportFlowProps,
  type DesignPlatformFlowState,
  type StepType,
  type PlatformBadgeProps,
  type StepControlsProps,
} from './types';
import styles from './DesignPlatformExportFlow.module.scss';

const INITIAL_FLOW_STATE: DesignPlatformFlowState = {
  selectedTemplateId: null,
  selectedMealtimeId: null,
  selectedVenueIds: [],
  menuTitleMode: 'venue_name',
  menuTitleOverride: '',
  selectedPriceSchemeId: null,
};

const STEP_MAP_WITH_MEALTIMES: Record<number, StepType> = {
  1: 'template',
  2: 'mealtime',
  3: 'venue',
  4: 'title',
  5: 'price-scheme',
  6: 'truncation',
  7: 'summary',
};

const STEP_MAP_WITHOUT_MEALTIMES: Record<number, StepType> = {
  1: 'template',
  2: 'venue',
  3: 'title',
  4: 'price-scheme',
  5: 'truncation',
  6: 'summary',
};

const PlatformBadge = ({ platformName, platformLogo }: PlatformBadgeProps): ReactElement => (
  <div className={styles['platform-badge']}>
    {platformLogo && <span className={styles['platform-logo']}>{platformLogo}</span>}
    <Typography size="medium" weight="regular">
      Connected with {platformName}
    </Typography>
  </div>
);

const StepControls = ({
  step,
  onBack,
  onContinue,
  onCancel,
  continueDisabled = false,
  isGenerating = false,
  isLastStep = false,
}: StepControlsProps): ReactElement => {
  const isFirstStep = step === 1;
  const leftButtonLabel = isFirstStep ? 'Cancel' : 'Back';
  const leftButtonAction = isFirstStep ? onCancel : onBack;

  const rightButtonLabel = isLastStep ? 'Generate PDF' : 'Continue';

  return (
    <div className={styles['step-controls']}>
      <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={leftButtonAction}>
        {leftButtonLabel}
      </SecondaryButton>
      <PrimaryButton
        size="full-width"
        type="button"
        onClick={onContinue}
        loading={isGenerating}
        disabled={continueDisabled || isGenerating}
      >
        {isGenerating ? 'Generating...' : rightButtonLabel}
      </PrimaryButton>
    </div>
  );
};

export const DesignPlatformExportFlow = ({
  menuName,
  templates,
  templatesLoading,
  mealtimes,
  venues,
  venuesLoading,
  venueCategories,
  venueCategoryOptions,
  priceSchemes,
  priceSchemesLoading,
  platformName,
  platformLogo,
  menu,
  onSubmit,
  onCancel,
}: DesignPlatformExportFlowProps): ReactElement => {
  const [currentStep, setCurrentStep] = useState(1);
  const [flowState, setFlowState] = useState<DesignPlatformFlowState>(INITIAL_FLOW_STATE);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const hasMealtimes = mealtimes.length > 0;
  const totalSteps = hasMealtimes ? 7 : 6;

  // Map logical step to actual step component
  const getStepComponent = useCallback(
    (step: number): StepType => {
      const stepMap = hasMealtimes ? STEP_MAP_WITH_MEALTIMES : STEP_MAP_WITHOUT_MEALTIMES;
      return stepMap[step];
    },
    [hasMealtimes],
  );

  const currentStepType = getStepComponent(currentStep);
  const isLastStep = currentStep === totalSteps;
  const isSummaryStep = currentStepType === 'summary';

  const getStepTitle = useCallback((): string => {
    switch (currentStepType) {
      case 'template':
        return 'Select a brand template';
      case 'mealtime':
        return 'Select a Mealtime';
      case 'venue':
        return 'Select a Venue';
      case 'title':
        return 'Menu Title';
      case 'price-scheme':
        return 'Price Scheme';
      case 'truncation':
        return 'Content Review';
      case 'summary':
        return 'Summary for ';
      default:
        return '';
    }
  }, [currentStepType]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === flowState.selectedTemplateId) || null,
    [templates, flowState.selectedTemplateId],
  );

  const isContinueDisabled = useMemo((): boolean => {
    switch (currentStepType) {
      case 'template':
        return !flowState.selectedTemplateId;
      case 'mealtime':
        return !flowState.selectedMealtimeId;
      case 'venue':
        return flowState.selectedVenueIds.length === 0;
      case 'price-scheme':
        return !flowState.selectedPriceSchemeId;
      case 'summary':
        return !selectedTemplate;
      default:
        return false;
    }
  }, [currentStepType, flowState, selectedTemplate]);

  const handleContinue = async () => {
    if (isLastStep && selectedTemplate) {
      setIsGenerating(true);
      setSubmitError(null);

      try {
        await onSubmit({
          destinations: ['MENU_PDF'],
          asset: {
            id: selectedTemplate.id,
            type: selectedTemplate.type,
          },
          venue_ids: flowState.selectedVenueIds,
          mealtime_id: flowState.selectedMealtimeId,
          menu_title: {
            mode: flowState.menuTitleMode,
            override: flowState.menuTitleMode === 'override' ? flowState.menuTitleOverride : null,
          },
          price_scheme_id: flowState.selectedPriceSchemeId as string,
        });
      } catch {
        setSubmitError('Failure to Generate PDF. Please try again');
      } finally {
        setIsGenerating(false);
      }
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
    setSubmitError(null);
  };

  const handleCancel = () => {
    setFlowState(INITIAL_FLOW_STATE);
    setCurrentStep(1);
    setSubmitError(null);
    onCancel();
  };

  const handleSelectTemplate = (templateId: string) => {
    setFlowState((prev) => ({ ...prev, selectedTemplateId: templateId }));
  };

  const handleSelectMealtime = (mealtimeId: string) => {
    setFlowState((prev) => ({ ...prev, selectedMealtimeId: mealtimeId }));
  };

  const handleToggleVenue = (venueId: string) => {
    setFlowState((prev) => {
      const isSelected = prev.selectedVenueIds.includes(venueId);
      return {
        ...prev,
        selectedVenueIds: isSelected
          ? prev.selectedVenueIds.filter((id) => id !== venueId)
          : [...prev.selectedVenueIds, venueId],
      };
    });
  };

  const handleSelectAllVenues = (venueIds: string[]) => {
    setFlowState((prev) => ({
      ...prev,
      selectedVenueIds: Array.from(new Set([...prev.selectedVenueIds, ...venueIds])),
    }));
  };

  const handleDeselectVenues = (venueIds: string[]) => {
    setFlowState((prev) => ({
      ...prev,
      selectedVenueIds: prev.selectedVenueIds.filter((id) => !venueIds.includes(id)),
    }));
  };

  const handleSelectTitleMode = (mode: 'venue_name' | 'override') => {
    setFlowState((prev) => ({ ...prev, menuTitleMode: mode }));
  };

  const handleChangeTitleOverride = (value: string) => {
    setFlowState((prev) => ({ ...prev, menuTitleOverride: value }));
  };

  const handleSelectPriceScheme = (priceSchemeId: string) => {
    setFlowState((prev) => ({ ...prev, selectedPriceSchemeId: priceSchemeId }));
  };

  const renderStepContent = (): ReactElement => {
    switch (currentStepType) {
      case 'template':
        return (
          <SelectTemplateStep
            templates={templates}
            templatesLoading={templatesLoading}
            selectedTemplateId={flowState.selectedTemplateId}
            onSelectTemplate={handleSelectTemplate}
          />
        );
      case 'mealtime':
        return (
          <SelectMealtimeStep
            mealtimes={mealtimes}
            selectedMealtimeId={flowState.selectedMealtimeId}
            onSelectMealtime={handleSelectMealtime}
          />
        );
      case 'venue':
        return (
          <SelectVenueStep
            venues={venues}
            venuesLoading={venuesLoading}
            venueCategories={venueCategories}
            venueCategoryOptions={venueCategoryOptions}
            selectedVenueIds={flowState.selectedVenueIds}
            onToggleVenue={handleToggleVenue}
            onSelectAll={handleSelectAllVenues}
            onDeselectVenues={handleDeselectVenues}
          />
        );
      case 'title':
        return (
          <MenuTitleStep
            menuTitleMode={flowState.menuTitleMode}
            menuTitleOverride={flowState.menuTitleOverride}
            onSelectMode={handleSelectTitleMode}
            onChangeOverride={handleChangeTitleOverride}
          />
        );
      case 'price-scheme':
        return (
          <SelectPriceSchemeStep
            priceSchemes={priceSchemes}
            priceSchemesLoading={priceSchemesLoading}
            selectedPriceSchemeId={flowState.selectedPriceSchemeId}
            onSelectPriceScheme={handleSelectPriceScheme}
          />
        );
      case 'truncation':
        return (
          <TruncationWarningsStep
            template={selectedTemplate}
            menu={menu}
            channelName={platformName}
            selectedMealtimeId={flowState.selectedMealtimeId}
          />
        );
      case 'summary':
        return (
          <SummaryStep
            flowState={flowState}
            templates={templates}
            mealtimes={mealtimes}
            venues={venues}
            priceSchemes={priceSchemes}
            hasMealtimes={hasMealtimes}
            error={submitError}
          />
        );
      default:
        return <div />;
    }
  };

  return (
    <div className={styles['design-platform-export-flow']}>
      <div className={styles['flow-header']}>
        <PlatformBadge platformName={platformName} platformLogo={platformLogo} />
        {!isSummaryStep && (
          <Typography size="xsmall" className={styles['step-indicator']}>
            Step {currentStep} of {totalSteps}
          </Typography>
        )}
      </div>

      <div className={styles['flow-title']}>
        {isSummaryStep ? (
          <Typography size="xxlarge" weight="semi-bold">
            {getStepTitle()}
            <span className={styles['menu-name-bold']}>{menuName}</span>
          </Typography>
        ) : (
          <Typography size="xxlarge" weight="semi-bold">
            {getStepTitle()}
          </Typography>
        )}
      </div>

      <div className={styles['flow-content']}>{renderStepContent()}</div>

      <StepControls
        step={currentStep}
        totalSteps={totalSteps}
        onBack={handleBack}
        onContinue={handleContinue}
        onCancel={handleCancel}
        continueDisabled={isContinueDisabled}
        isGenerating={isGenerating}
        isLastStep={isLastStep}
      />
    </div>
  );
};
